import { nextTick, onUnmounted } from 'vue';
import { claimRouteReadiness } from '@/autopilot/routeReadiness';

/**
 * Page-side unlock of the route readiness lock (see `routeReadiness.ts`).
 *
 * Every navigation locks autopilot function responses until the destination page is ready. A page
 * that resolves context asynchronously claims the transition by calling this composable during
 * setup, and calls the returned `ready()` once its resources have landed in context (e.g. from a
 * list's `after-fetch`). The page decides what "ready" means — a page waiting on several
 * independent resources can call this composable once per resource; the route unlocks when the
 * last one is ready.
 *
 * `ready()` is idempotent (safe to call on every refetch) and waits a tick so freshly-written
 * context has been republished and synced to the client before the lock releases. Unmounting
 * releases automatically, so a claim never strands past the page; the client's
 * `contextResolutionTimeoutMs` remains the final backstop.
 */
export function useAutopilotRouteReady(): () => void {
  const claim = claimRouteReadiness();
  const ready = () => {
    // let pending watchers republish + sync the page's context before the lock releases
    nextTick().then(claim);
  };
  // never strand the lock past the page
  onUnmounted(() => claim());
  return ready;
}
